
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CompletedBooking {
  id: string;
  booking_id: number;
  service_id: number;
  service_name: string;
  customer_id: string;
  customer_name: string;
  booking_start_time: string;
  completed_at: string;
  payment_amount: number;
  payment_status: string;
  rating: number;
  comment: string | null;
}

export const useCompletedBookings = () => {
  const [data, setData] = useState<CompletedBooking[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCompletedBookings = async () => {
      try {
        setIsLoading(true);
        
        // Get bookings with completion data joined
        const { data: completions, error } = await supabase
          .from('booking_completions')
          .select(`
            id, 
            booking_id, 
            payment_amount, 
            payment_status, 
            created_at, 
            rating, 
            comment,
            bookings!booking_completions_booking_id_fkey (
              service_id, 
              customer_name, 
              customer_id, 
              start_time
            )
          `)
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        console.log("🔍 Fetched completions raw:", completions);
        
        if (!completions || completions.length === 0) {
          console.log("No completed bookings found");
          setData([]);
          return;
        }
        
        // Get service IDs to fetch service names
        const serviceIds = completions
          .map(completion => completion.bookings?.service_id)
          .filter(id => id !== undefined && id !== null);
          
        // Fetch service names in a separate query
        const { data: services, error: servicesError } = await supabase
          .from('services')
          .select('id, name')
          .in('id', serviceIds);
          
        if (servicesError) throw servicesError;
        
        console.log("Fetched services:", services);
        
        // Create a map of service id to name for easy lookup
        const serviceMap = {};
        if (services) {
          services.forEach(service => {
            serviceMap[service.id] = service.name;
          });
        }
        
        // Transform the data into the format we need
        const transformedData = completions.map(completion => {
          const serviceId = completion.bookings?.service_id;
          // Look up the service name using the service ID
          const serviceName = serviceId && serviceMap[serviceId] 
            ? serviceMap[serviceId] 
            : "Unknown Service";
            
          return {
            id: completion.id,
            booking_id: completion.booking_id,
            service_id: serviceId,
            service_name: serviceName,
            customer_id: completion.bookings?.customer_id,
            customer_name: completion.bookings?.customer_name || "Unknown Customer",
            booking_start_time: completion.bookings?.start_time,
            completed_at: completion.created_at,
            payment_amount: completion.payment_amount,
            payment_status: completion.payment_status,
            rating: completion.rating,
            comment: completion.comment,
          };
        });
        
        console.log("Transformed data:", transformedData);
        setData(transformedData);
      } catch (err) {
        console.error("Error fetching completed bookings:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompletedBookings();
  }, []);

  return { data, isLoading, error };
};
